import * as _ from 'lodash';
import { type VoidFunctionComponent } from 'react';

import { plural } from '../../../../../../../shared/helpers/plural';
import { RevisionLicenseConclusionScope } from '../../../../../../../shared/model-typings/revisionlicenseconclusion';
import { useDeleteScopedConclusion } from '../../../../../../hooks/api/licenseConclusions/useDeleteScopedConclusion';
import { icons } from '../../../../../generic';
import Button, { BUTTON_TYPE } from '../../../../../generic/Button';
import TooltipIcon from '../../../../../generic/TooltipIcon';
import type { ConclusionData } from '../../../../../../../shared/types/licenses';

import type { LicenseTabScope } from './types';

import styles from './DependencyLicenses.module.less';

type ConclusionBannerProps = {
  dependencyRevisionLocator: string;
  scope: LicenseTabScope;
  concludedLicenses: ConclusionData;
  allLicenses: Set<string>;
  canEditLicenses: boolean;
  entityName?: string;
};

export const ConclusionBanner: VoidFunctionComponent<ConclusionBannerProps> = ({
  dependencyRevisionLocator,
  scope,
  concludedLicenses,
  allLicenses,
  canEditLicenses,
  entityName = 'dependency',
}) => {
  let scopeType: RevisionLicenseConclusionScope | undefined;
  let userRevisionLocator: string | undefined;

  if (scope.type === 'project') {
    scopeType = RevisionLicenseConclusionScope.PROJECT;
    userRevisionLocator = scope.revisionLocator;
  } else if (scope.type === 'revision') {
    scopeType = RevisionLicenseConclusionScope.REVISION;
  } else if (scope.type === 'releaseGroup') {
    scopeType = RevisionLicenseConclusionScope.RELEASE_GROUP;
  }
  const { mutateAsync: deleteScopedConclusion, isPending: isDeletingScopedConclusion } =
    useDeleteScopedConclusion(dependencyRevisionLocator, userRevisionLocator);

  const onRevertConclusion = () => {
    let scopeId: string | null = null;
    if (scope.type === 'project') {
      scopeId = scope.projectLocator;
    } else if (scope.type === 'revision') {
      scopeId = scope.revisionLocator;
    } else if (scope.type === 'releaseGroup') {
      scopeId = scope.releaseGroupId.toString();
    }
    if (scopeType && scopeId) {
      deleteScopedConclusion({
        dependencyRevisionLocator,
        scope: scopeType,
        scopeId,
      });
    }
  };

  const baseConclusions = concludedLicenses.base;
  const scopedConclusions = concludedLicenses.scoped;

  const hasOverride = !_.isEqual(scopedConclusions.licenses, baseConclusions.licenses);
  const hasBaseConclusion = baseConclusions.licenses.length > 0;
  // We only show the revert button if the base conclusion we'd be reverting to is in the current list of licenses.
  // This avoids the case where a non-existent license is concluded, and users can't unconclude it.
  const areBaseConclusionsInLicenses = baseConclusions.licenses.every((license) => allLicenses.has(license));

  const shouldShowRevertButton =
    canEditLicenses && areBaseConclusionsInLicenses && !isDeletingScopedConclusion && hasBaseConclusion;

  return (
    <div className={styles.conclusionBanner}>
      <div className={styles.title}>
        Concluded Licenses{' '}
        <TooltipIcon
          id="conclusion-banner-tooltip"
          icon={icons.circleInfo}
          tooltip={`Licenses that you are declaring as governing this ${entityName}. This is not legal advice.`}
        />
      </div>
      <div>
        <div className={styles.concludedLicenses}>{scopedConclusions.licenses.join(' AND ')}</div>
        {scopedConclusions.licenses.length === 0 && <div>No licenses have been concluded.</div>}
        {hasOverride && (
          <div className={styles.details}>
            <div>
              {scopedConclusions.lastEditedBy &&
                `${scopedConclusions.lastEditedBy} manually concluded ${plural(scopedConclusions.licenses.length, 'this license', 'these licenses')}.`}
            </div>
            <div>
              {hasBaseConclusion && `FOSSA originally concluded this as ${baseConclusions.licenses.join(' AND ')}. `}
              {shouldShowRevertButton && (
                <Button type={BUTTON_TYPE.TEXT} onClick={onRevertConclusion}>
                  <span className={styles.buttonText}>Revert Conclusion</span>
                </Button>
              )}
            </div>
          </div>
        )}
        {!hasOverride && baseConclusions.licenses.length > 0 && (
          <div className={styles.details}>
            Concluded by FOSSA as the most accurate license based on available data.
          </div>
        )}
      </div>
    </div>
  );
};
